import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Filter, CreditCard } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../hooks/useAuth';
import { ROLES } from '../../utils/roles';
import * as ordersApi from '../../api/orders.api';
import * as vendorApi from '../../api/vendor.api';
import { errorMessage } from '../../api/client';
import { formatMoney, formatDate, formatTime12 } from '../../utils/format';

function Orders() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const role = user?.role;
  const isAdminish = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.STAFF].includes(role);
  const isVendor = role === ROLES.VENDOR;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        let data;
        if (isVendor) data = await vendorApi.listMyOrders(statusFilter ? { status: statusFilter } : {});
        else if (isAdminish) data = await ordersApi.listAdminOrders(statusFilter ? { status: statusFilter } : {});
        else data = await ordersApi.myOrders();
        if (!cancelled) setRows(data || []);
      } catch (e) {
        if (!cancelled) setErr(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [isVendor, isAdminish, statusFilter]);

  const columns = [
    {
      key: 'orderNumber',
      header: 'Order #',
      render: (r) => <Link to={`/orders/${r._id}`} className="cell-strong cell-mono">{r.orderNumber}</Link>
    },
    {
      key: 'package',
      header: 'Package',
      render: (r) => <span>{r.package?.name || r.packageSnapshot?.name || '—'}</span>
    },
    ...(isAdminish || isVendor ? [{
      key: 'patient',
      header: 'User',
      render: (r) => {
        // Client 2026-08-28: patient identifiers are masked in vendor
        // responses until the patient checks in at the clinic. Show a
        // clear "Hidden" chip in place of a blank cell.
        if (r.user?._piiHidden) {
          return (
            <span className="badge neutral" title="Patient details unlock after check-in">
              Hidden until check-in
            </span>
          );
        }
        return (
          <div style={{ maxWidth: 220 }}>
            <div className="cell-strong cell-truncate" title={r.user?.name || ''}>{r.user?.name || '—'}</div>
            <div className="muted text-xs cell-truncate" title={r.user?.email || ''}>{r.user?.email}</div>
          </div>
        );
      }
    }] : []),
    // Vendor-only columns — client 2026-08-06: clinics need the
    // AIH-XXXX booking ID (for check-in) and the actual appointment
    // date/time (in 12-hour AM/PM) at a glance, not just the order's
    // creation date.
    ...(isVendor ? [{
      key: 'bookingId',
      header: 'Booking #',
      render: (r) => r.bookingId
        ? <span className="cell-mono">{r.bookingId}</span>
        : <span className="muted">—</span>,
    }, {
      key: 'appointment',
      header: 'Appointment',
      render: (r) => {
        if (!r.scheduledAt) return <span className="muted">—</span>;
        return (
          <div>
            <div className="cell-strong">{formatDate(r.scheduledAt)}</div>
            <div className="muted text-xs">
              {formatTime12(r.scheduledAt)}
              {r.scheduledSlot ? ` · ${r.scheduledSlot}` : ''}
            </div>
          </div>
        );
      },
    }] : []),
    // Patient-only columns — client 2026-09-10: users need to know which
    // clinic each booking is with, and when the appointment is scheduled,
    // straight from the list. Without these, multiple ASSIGNED rows all
    // look the same.
    ...(!isVendor && !isAdminish ? [{
      key: 'clinic',
      header: 'Clinic',
      render: (r) => {
        const v = r.assignedVendor;
        if (!v || typeof v !== 'object') return <span className="muted">—</span>;
        const name = v.clinicProfile?.clinicName || v.name || 'Clinic';
        const loc  = [v.clinicProfile?.city, v.clinicProfile?.state].filter(Boolean).join(', ');
        return (
          <div style={{ maxWidth: 220 }}>
            <div className="cell-strong cell-truncate" title={name}>{name}</div>
            {loc && <div className="muted text-xs cell-truncate" title={loc}>{loc}</div>}
          </div>
        );
      },
    }, {
      key: 'appointment',
      header: 'Appointment',
      render: (r) => {
        if (!r.scheduledAt) return <span className="muted">Not scheduled</span>;
        return (
          <div>
            <div className="cell-strong">{formatDate(r.scheduledAt)}</div>
            <div className="muted text-xs">
              {formatTime12(r.scheduledAt)}
              {r.scheduledSlot ? ` · ${r.scheduledSlot}` : ''}
            </div>
          </div>
        );
      },
    }] : []),
    {
      key: 'amount',
      header: 'Amount',
      render: (r) => <span>{formatMoney(r.amount, r.currency || 'MYR')}</span>
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <StatusBadge status={r.status} />
    },
    ...(isAdminish ? [{
      key: 'paymentStatus',
      header: 'Payment',
      render: (r) => <StatusBadge status={r.paymentStatus} />
    }] : []),
    {
      key: 'createdAt',
      header: isVendor ? 'Booking created' : 'Created',
      render: (r) => <span className="muted">{formatDate(r.createdAt)}</span>
    },
    {
      key: 'actions',
      header: '',
      width: 160,
      // Client 2026-09-10: patients need an obvious "Pay now" affordance
      // directly on PENDING_PAYMENT rows — previously the only way in was
      // the eye-icon → OrderDetails → header button, which most users
      // never discovered.
      render: (r) => {
        const isPending = r.paymentStatus !== 'PAID' && r.status === 'PENDING_PAYMENT';
        return (
          <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {!isAdminish && !isVendor && isPending && (
              <Link
                to={`/orders/${r._id}`}
                className="btn btn-primary"
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                <CreditCard size={12} /> Pay now
              </Link>
            )}
            <Link to={`/orders/${r._id}`} className="icon-btn" aria-label="View order">
              <Eye size={16} />
            </Link>
          </div>
        );
      },
    }
  ];

  return (
    <>
      <PageHeader
        title={isVendor ? 'Assigned bookings' : isAdminish ? 'Bookings' : 'My bookings'}
        subtitle={
          isVendor ? 'Bookings routed to your clinic. Manage check-ins, run consultations, and upload patient progress.'
          : isAdminish ? 'All bookings across the platform.'
          : 'Your bookings and their progress.'
        }
      />

      <DataTable
        loading={loading}
        rows={rows}
        columns={columns}
        searchPlaceholder="Search by booking number, plan, customer…"
        toolbarRight={
          <select
            className="select"
            style={{ width: 180 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="PENDING_PAYMENT">Pending payment</option>
            <option value="PAID">Paid</option>
            <option value="ASSIGNED">Assigned</option>
            <option value="IN_PROGRESS">In progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        }
        emptyTitle={isVendor ? 'No assigned orders yet' : 'No orders yet'}
      />

      {err && <div className="notice warning" style={{ marginTop: 'var(--s-4)' }}>{err}</div>}
    </>
  );
}

export default Orders;
